import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service, SourceInfo } from 'webapp-types';
import { getRelativeTime } from 'webapp/utils/number';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import type { UnusedItemModel } from 'webapp/components/metrics/unused/popup';
import UsedSourcesListComponent from 'webapp/components/used-sources-list';

import CustomQuery from 'webapp/models-custom/custom-query';
import HorizonMetric from 'webapp/models-custom/horizon-metric';
import Script from 'webapp/models-custom/script';

interface Args {
	model: UnusedItemModel;
	selected: UnusedItemModel[];
	onAddToSelected: (item: UnusedItemModel) => void;
	onRemoveFromSelected: (item: UnusedItemModel) => void;
}

export default class MetricsUnusedListRowComponent extends Component<Args> {
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;

	get isChecked(): boolean {
		return this.args.selected.map((s) => s.id).includes(this.args.model.id);
	}

	get metricName(): string {
		const model = this.args.model;
		if (this.isScript(model)) {
			return model.description;
		} else if (this.isCustomQuery(model)) {
			return model.queryName;
		} else if (this.isHorizonMetric(model)) {
			return model.metricName;
		}
		return '';
	}

	get metricIcon(): IconName | null {
		const model = this.args.model;
		if (this.isScript(model)) {
			return 'Calculator';
		} else if (this.isCustomQuery(model)) {
			return 'Data';
		}
		return null;
	}

	get createdText(): string {
		const model = this.args.model;
		return getRelativeTime(model.created);
	}

	get createdByText(): string {
		const model = this.args.model;
		return model.createdByText;
	}

	get usedSources() {
		const model = this.args.model;
		let sourcesInfo: SourceInfo[] = [];

		if (this.isScript(model)) {
			sourcesInfo = model.usedSources.map((source) => {
				return {
					label: source.title,
					avatar: source.realAvatar,
				};
			});
		} else if (this.isCustomQuery(model)) {
			const sourceRecord = model.source;
			if (sourceRecord) {
				const sourceCM = this.mmSourcesService.createModelFromRecord(sourceRecord);
				sourcesInfo.push({
					label: sourceCM.title,
					avatar: sourceCM.realAvatar,
				});
			}
		} else if (this.isHorizonMetric(model)) {
			sourcesInfo = model.usedSources.map((source) => {
				return {
					label: source.title,
					avatar: source.realAvatar,
				};
			});
		}

		return sourcesInfo;
	}

	@action
	changeSelection(value: boolean): void {
		const model = this.args.model;

		if (value) {
			this.args.onAddToSelected(model);
		} else {
			this.args.onRemoveFromSelected(model);
		}
	}

	private isScript(model: UnusedItemModel): model is Script {
		return model instanceof Script;
	}

	private isCustomQuery(model: UnusedItemModel): model is CustomQuery {
		return model instanceof CustomQuery;
	}

	private isHorizonMetric(model: UnusedItemModel): model is HorizonMetric {
		return model instanceof HorizonMetric;
	}
	<template>
		<tr class="item {{if this.isChecked 'item--selected'}}">
			<td>
				<FormCheckboxComponent
					@selected={{this.isChecked}}
					@theme="red"
					@onChange={{this.changeSelection}}
				/>
				<span class="metric">
					{{this.metricName}}
					{{#if this.metricIcon}}
						<CommonUiIconComponent @name={{this.metricIcon}} @size="sm" />
					{{/if}}
				</span>
			</td>
			<td>
				<UsedSourcesListComponent @sources={{this.usedSources}} @size="sm" />
			</td>
			<td>{{this.createdByText}}</td>
			<td>{{this.createdText}}</td>
		</tr>
	</template>
}
